/* eslint-disable */
import { useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import useSWR from 'swr';
import { NumberParam, NumericArrayParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { TablePagination } from '@material-ui/core';
import { Header, Container, ExportReportWrapper } from '../../styles/shared/styles';
import DatePeriodFilter from '../../components/FiIndicationReportTableList/Filter';
import FiIndicationReportTableList from '../../components/FiIndicationReportTableList';
import FiIndicationSummary from './FiIndicationSummary';
import { variantFetcher } from '../../service/SWRFetcher';
import useApp from '../../hooks/useApp';
import { excelExport } from '../../service/excel.service';
import { reportQueryOptions } from '../../utils/reportQueryParams';

const FiIndicationReport = () => {
  const ref = useRef(null);
  const { setState } = useApp();
  const history = useHistory();

  // Defaults precisam ser estáveis entre renders, senão `removeDefaultsFromUrl`
  // compara contra uma data recém-criada a cada ciclo.
  const paramConfig = useMemo(
    () => ({
      date_ini: withDefault(StringParam, DateTime.now().plus({ months: -1 }).toISODate()),
      date_end: withDefault(StringParam, DateTime.now().toISODate()),
      partner_id: withDefault(NumericArrayParam, []),
      broker_id: withDefault(NumericArrayParam, []),
      inviter_id: withDefault(NumericArrayParam, []),
      insurance_id: withDefault(NumericArrayParam, []),
      proposal: withDefault(StringParam, ''),
      page: withDefault(NumberParam, 0),
      per_page: withDefault(NumberParam, 10),
    }),
    []
  );

  const [filters, setFilters] = useQueryParams(paramConfig, reportQueryOptions);

  const handleChangePage = (event, newPage) => setFilters({ page: newPage });

  const handleChangeRowsPerPage = (event) =>
    setFilters({ page: 0, per_page: parseInt(event.target.value, 10) });

  // Ao aplicar o filtro a paginação volta à primeira página.
  const applyFilters = (values) => setFilters({ ...values, page: 0 });

  const listParam = (key, ids) => (ids || []).map((id) => `&${key}[]=${id}`).join('');

  const queryParams = () =>
    `start_date=${filters.date_ini}&end_date=${filters.date_end}` +
    listParam('broker_id', filters.broker_id) +
    listParam('inviter_id', filters.inviter_id) +
    listParam('partner_id', filters.partner_id) +
    listParam('insurance_id', filters.insurance_id) +
    `&proposal=${filters.proposal}`;

  const { data, isValidating } = useSWR(
    `/leads/fi-indication-report?page=${filters.page}&per_page=${filters.per_page}&${queryParams()}`,
    variantFetcher,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    setState('loading', isValidating);
  }, [isValidating]);

  useEffect(() => {
    if (filters.page || filters.per_page > 10) {
      ref?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filters.page, filters.per_page, isValidating]);

  // Drill-down: abre as propostas indicadas pelo consultor no relatório de
  // Concessionária, levando junto a URL de retorno deste relatório.
  const openInviterProposals = (inviterId) => {
    if (!inviterId) return;

    const target = new URLSearchParams();
    target.set('date_ini', filters.date_ini);
    target.set('date_end', filters.date_end);
    (filters.partner_id || []).forEach((id) => target.append('partner_id', id));
    (filters.insurance_id || []).forEach((id) => target.append('insurance_id', id));
    target.set('inviter_id', inviterId);
    target.set('from', 'fi');
    target.set('fi_q', window.location.search.replace(/^\?/, ''));

    history.push({ pathname: '/relatorio-indicacoes-concessionaria', search: target.toString() });
  };

  return (
    <motion.div initial={{ opacity: 0, x: -100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }}>
      <Container style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Header>
          <h1>Relatório de Indicação F&I</h1>
        </Header>
        <DatePeriodFilter filters={filters} setFilters={applyFilters} />

        <FiIndicationSummary summary={data?.['indication-summary']} />

        <TablePagination
          ref={ref}
          component="div"
          count={data?.meta?.total || 0}
          page={filters.page}
          onPageChange={handleChangePage}
          rowsPerPage={filters.per_page}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        <FiIndicationReportTableList
          listItem={data?.data}
          totals={data?.totals}
          onSelectInviter={openInviterProposals}
        />

        {data?.data && data?.data?.length > 0 && (
          <ExportReportWrapper>
            <button
              className="button"
              onClick={() =>
                excelExport(
                  `/leads/fi-indication-report?${queryParams()}&download=true`,
                  'relatorio_indicacao_fi_' + DateTime.now().toFormat('dd_LL_yyyy')
                )
              }>
              Exportar
            </button>
          </ExportReportWrapper>
        )}
      </Container>
    </motion.div>
  );
};

export default FiIndicationReport;
